import { Controller } from "@hotwired/stimulus"

// Native HTML5 drag-and-drop reordering for a flat list of items.
//
// Markup contract:
//   <ul data-controller="survey-reorder"
//       data-survey-reorder-url-value="/path/to/reorder">
//     <li data-survey-reorder-target="item"
//         data-id="123"
//         draggable="true">
//       … <span data-survey-reorder-target="position">position 1</span> …
//     </li>
//   </ul>
//
// `data-id` is the canonical attribute. `data-variant-id` is still accepted as
// a fallback so older markup keeps working.
//
// On drop, the controller:
//   1. Updates each item's position label locally.
//   2. POSTs `{ positions: [{ id, position }, …] }` to the configured URL.
//
// The server is expected to respond 2xx; we don't render anything extra on success.
export default class extends Controller {
  static targets = ["item", "position"]
  static values = { url: String }

  itemId(item) {
    return item.dataset.id ?? item.dataset.variantId ?? ""
  }

  connect() {
    this.itemTargets.forEach((item) => {
      item.addEventListener("dragstart", this.onDragStart)
      item.addEventListener("dragend",   this.onDragEnd)
    })
    this.element.addEventListener("dragover", this.onDragOver)
  }

  disconnect() {
    this.itemTargets.forEach((item) => {
      item.removeEventListener("dragstart", this.onDragStart)
      item.removeEventListener("dragend",   this.onDragEnd)
    })
    this.element.removeEventListener("dragover", this.onDragOver)
  }

  onDragStart = (event) => {
    const item = event.currentTarget
    item.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
    // Required for Firefox to initiate a drag.
    event.dataTransfer.setData("text/plain", this.itemId(item))
  }

  onDragEnd = (event) => {
    event.currentTarget.classList.remove("opacity-50")
    this.relabelPositions()
    this.persistPositions()
  }

  onDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const dragged = this.element.querySelector(".opacity-50")
    if (!dragged) return

    const nextSibling = this.findNextSibling(event.clientY, dragged)
    if (nextSibling == null) {
      this.element.appendChild(dragged)
    } else if (nextSibling !== dragged) {
      this.element.insertBefore(dragged, nextSibling)
    }
  }

  findNextSibling(mouseY, dragged) {
    const siblings = this.itemTargets.filter((item) => item !== dragged)
    for (const item of siblings) {
      const { top, height } = item.getBoundingClientRect()
      if (mouseY < top + height / 2) return item
    }
    return null
  }

  relabelPositions() {
    this.itemTargets.forEach((item, index) => {
      const labelEl = item.querySelector('[data-survey-reorder-target="position"]')
      if (labelEl) labelEl.textContent = `position ${index + 1}`
    })
  }

  persistPositions() {
    if (!this.urlValue) return

    const positions = this.itemTargets.map((item, index) => ({
      id: Number(this.itemId(item)),
      position: index + 1,
    }))

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.urlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token || "",
      },
      body: JSON.stringify({ positions }),
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error("survey-reorder: failed to persist positions", err)
    })
  }
}
